import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import { dialogs } from "../../context/DialogContext";
import ModalEntregarLlaves from "./ModalEntregarLlaves";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function etiquetaUbicacion(u) {
  if (u === "pendiente_aceptacion") return "Pendiente de aceptación";
  if (u === "externo") return "Fuera (externo)";
  return "En oficina";
}

function colorUbicacion(u) {
  // Contraste en tema oscuro: naranja→amarillo, rojo→verde (legible sobre el tinte del fondo).
  if (u === "pendiente_aceptacion") {
    return { bg: "rgba(245,158,11,0.15)", fg: "#facc15", border: "rgba(250,204,21,0.45)" };
  }
  if (u === "externo") {
    return { bg: "rgba(239,68,68,0.12)", fg: "#4ade80", border: "rgba(74,222,128,0.4)" };
  }
  return { bg: "rgba(34,197,94,0.12)", fg: "#4ade80", border: "rgba(34,197,94,0.3)" };
}

export default function BloqueLlavesUbicacion({
  propiedadId,
  referencia,
  tenemosLlaves,
  theme,
  puedeModificar = true,
}) {
  const c = theme?.colors || {};
  const [estado, setEstado] = useState(null);
  const [modal, setModal] = useState(false);
  const [busy, setBusy] = useState(false);

  const cargar = useCallback(async () => {
    if (!propiedadId || !tenemosLlaves) {
      setEstado(null);
      return;
    }
    try {
      const { data } = await api.get(`/propiedades/${propiedadId}/llaves`);
      if (data?.ok) setEstado(data.data);
    } catch {
      /* noop */
    }
  }, [propiedadId, tenemosLlaves]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const ubicActual = estado?.propiedad?.llaves_ubicacion || null;

  // Mientras espera el SMS del externo: refrescar sola la ficha (sin pulsar Actualizar).
  useEffect(() => {
    if (!propiedadId || !tenemosLlaves) return undefined;
    if (ubicActual !== "pendiente_aceptacion") return undefined;

    const refrescarSiVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      cargar();
    };
    const id = setInterval(refrescarSiVisible, 4000);
    const onVis = () => {
      if (document.visibilityState === "visible") cargar();
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [propiedadId, tenemosLlaves, ubicActual, cargar]);

  if (!tenemosLlaves || !propiedadId) return null;

  const prop = estado?.propiedad || {};
  const ubic = prop.llaves_ubicacion || "oficina";
  const col = colorUbicacion(ubic);
  const citasRecientes = Array.isArray(estado?.citas_recientes) ? estado.citas_recientes.slice(0, 5) : [];

  const btn = (label, onClick, variant = "secondary") => (
    <button
      type="button"
      disabled={!puedeModificar || busy}
      onClick={onClick}
      style={{
        padding: "7px 11px",
        borderRadius: 8,
        border: variant === "primary" ? "none" : `1px solid ${c.border || "#d4d4d8"}`,
        background:
          variant === "primary"
            ? "linear-gradient(135deg, #0369a1, #0ea5e9)"
            : variant === "danger"
              ? "rgba(34,197,94,0.14)"
              : isDarkish(c)
                ? "transparent"
                : "#f8fafc",
        color: variant === "primary" ? "#fff" : variant === "danger" ? "#4ade80" : c.textPrimary,
        fontSize: 12,
        fontWeight: 700,
        cursor: !puedeModificar || busy ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        opacity: !puedeModificar ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );

  const reenviar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/propiedades/${propiedadId}/llaves/reenviar`);
      if (data?.data?.sms_error) {
        await dialogs.alert(`Reenviado con aviso SMS: ${data.data.sms_error}`);
      }
      setEstado(data.data);
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "No se pudo reenviar");
    } finally {
      setBusy(false);
    }
  };

  const aceptarPersona = async () => {
    if (!(await dialogs.confirm("¿Marcar como aceptado en persona (sin SMS)?"))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/propiedades/${propiedadId}/llaves/aceptar-en-persona`);
      setEstado(data.data);
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const devolver = async () => {
    if (!(await dialogs.confirm("¿Confirmar que las llaves han vuelto a la oficina?"))) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/propiedades/${propiedadId}/llaves/devolver`);
      setEstado(data.data);
    } catch (e) {
      await dialogs.alert(e.response?.data?.error || e.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        gridColumn: "1 / -1",
        marginTop: 4,
        padding: "12px 14px",
        borderRadius: 10,
        border: `1px solid ${col.border}`,
        background: col.bg,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: col.fg }}>
          🔑 {etiquetaUbicacion(ubic)}
        </span>
        {prop.llaves_con_quien && (
          <span style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary }}>
            · {prop.llaves_con_quien}
          </span>
        )}
        {prop.llaves_desde && (
          <span style={{ fontSize: 12, color: c.textMuted }}>desde {fmtWhen(prop.llaves_desde)}</span>
        )}
        {prop.llaves_telefono && (
          <span style={{ fontSize: 12, color: c.textMuted }}>· {prop.llaves_telefono}</span>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {ubic === "oficina" && btn("Entregar a externo", () => setModal(true), "primary")}
        {ubic === "pendiente_aceptacion" && (
          <>
            {btn("Reenviar SMS", reenviar)}
            {btn("Aceptó en persona", aceptarPersona)}
            {btn("Han vuelto", devolver, "danger")}
          </>
        )}
        {ubic === "externo" && btn("Han vuelto a oficina", devolver, "danger")}
      </div>

      {citasRecientes.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.06em", color: c.textMuted, marginBottom: 6 }}>
            ÚLTIMAS CITAS (quién usó las llaves)
          </div>
          {citasRecientes.map((cita) => (
            <div key={cita.id} style={{ fontSize: 12, color: c.textSecondary, marginBottom: 4, lineHeight: 1.35 }}>
              <strong style={{ color: c.textPrimary }}>{cita.agente_nombre || "Agente sin nombre"}</strong>
              {cita.fecha_inicio ? ` · ${fmtWhen(cita.fecha_inicio)}` : ""}
              {cita.tipo ? ` · ${cita.tipo}` : ""}
              {cita.cliente_display ? ` · ${cita.cliente_display}` : ""}
              {cita.estado ? ` · ${cita.estado}` : ""}
            </div>
          ))}
        </div>
      )}

      <ModalEntregarLlaves
        isOpen={modal}
        onClose={() => setModal(false)}
        propiedadId={propiedadId}
        referencia={referencia}
        theme={theme}
        onDone={async (data, meta) => {
          setEstado(data);
          if (meta?.smsError) {
            await dialogs.alert(
              `Entrega registrada, pero el SMS falló: ${meta.smsError}. Puedes pulsar «Reenviar SMS» o «Aceptó en persona».`
            );
          }
        }}
      />
    </div>
  );
}

function isDarkish(c) {
  return Boolean(c?.bgCard && String(c.bgCard).includes("#1"));
}
